<script setup lang="ts">
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';

  import { ComboSelect } from '@ui/components/combo-select';
  import { useLoader } from '@ui/components/combo-select/useLoader';

  import { useBusinessUnits } from '@/composables/useBusinessUnits';

  interface BusinessUnitOption {
    id: string;
    name: string;
  }

  const emit = defineEmits<{
    (e: 'update', value: string[] | undefined): void;
  }>();

  const route = useRoute();
  const { businessUnits } = useBusinessUnits();
  const selectedBusinessUnits = computed(() => {
    const units = businessUnits.value;
    if (!units.length) return [];

    const queryValue = route.query?.business_unit_id__in;
    if (!queryValue) return [];

    const ids = Array.isArray(queryValue) ? queryValue : [queryValue];
    return ids
      .filter((id): id is string => typeof id === 'string')
      .map((id) => units.find((bu) => bu.id === id) ?? { id, name: id });
  });

  const businessUnitLoader = useLoader({
    load: async (args?) => {
      const units = businessUnits.value;
      if (!args?.search) return units;

      const searchTerm = args.search.toLowerCase();
      return units.filter((bu) => bu.name?.toLowerCase()?.includes(searchTerm));
    },
  });
</script>

<template>
  <ComboSelect
    :model-value="selectedBusinessUnits"
    :loader="businessUnitLoader"
    searchable
    :multiple="true"
    variant="filter"
    placeholder="Business Unit"
    search-placeholder="Filter by business unit..."
    option-identifier="name"
    @update:model-value="
      (selection) =>
        emit(
          'update',
          Array.isArray(selection) && selection.length ? selection.map((bu) => bu.id) : undefined,
        )
    "
  >
    <template #filter-label>
      <i class="fas fa-building text-xs" />
      <span> Business Unit </span>
    </template>
  </ComboSelect>
</template>
